import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "../ui/sheet";
import { Logo } from "./Logo";
import { FEATURES, featurePath } from "../../data/site";

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/#meet-honesti", label: "Meet Honesti", hash: true },
  { to: "/features", label: "Features", menu: true },
  { to: "/advanced-learning", label: "Advanced Learning" },
];

const FeatureMenu = () => (
  <div className="invisible absolute left-1/2 top-full w-[340px] -translate-x-1/2 pt-4 opacity-0 transition-[opacity,visibility] duration-300 group-hover:visible group-hover:opacity-100">
    <div className="glass rounded-2xl p-2">
      <Link to="/features" data-testid="nav-menu-overview" className="block rounded-xl px-4 py-3 text-sm font-semibold text-cyan-200 hover:bg-white/5">Features overview</Link>
      {FEATURES.map((f) => (
        <Link key={f.slug} to={featurePath(f.slug)} data-testid={`nav-menu-${f.slug}`} className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-slate-200 transition-colors hover:bg-white/5 hover:text-white">
          <f.icon className="h-4 w-4 text-cyan-300" /> {f.title}
        </Link>
      ))}
    </div>
  </div>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const isActive = (l) => (l.hash ? pathname === "/" && hash === "#meet-honesti" : l.end ? pathname === "/" && !hash : pathname.startsWith(l.to));

  return (
    <header data-testid="site-nav" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${scrolled ? "border-b border-cyan-400/15 bg-ink/75 backdrop-blur-xl" : "border-b border-transparent"}`}>
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 lg:px-12">
        <Link to="/" data-testid="nav-logo" aria-label="HonestChoice AI home"><Logo /></Link>
        <nav className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <div key={l.label} className={l.menu ? "group relative" : "relative"}>
              <NavLink to={l.to} end={l.end} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`} className={`flex items-center gap-1 py-2 text-[0.8rem] font-medium uppercase tracking-[0.18em] transition-colors ${isActive(l) ? "text-white" : "text-slate-300 hover:text-white"}`}>
                {l.label}{l.menu && <ChevronDown className="h-3.5 w-3.5" />}
              </NavLink>
              {isActive(l) && <motion.span layoutId="nav-underline" className="absolute -bottom-1 left-0 right-0 h-[2px] rounded-full bg-cyan-300 shadow-[0_0_12px_#22d3ee]" />}
              {l.menu && <FeatureMenu />}
            </div>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/waitlist" data-testid="nav-waitlist-btn" className="btn-primary hidden !px-6 !py-2.5 text-sm sm:inline-flex">Join Waitlist <ArrowRight className="h-4 w-4" /></Link>
          <MobileMenu />
        </div>
      </div>
    </header>
  );
};

const MobileMenu = () => (
  <Sheet>
    <SheetTrigger asChild>
      <button data-testid="mobile-menu-btn" aria-label="Open menu" className="glow-edge grid h-11 w-11 place-items-center rounded-full bg-deep/70 text-white lg:hidden"><Menu className="h-5 w-5" /></button>
    </SheetTrigger>
    <SheetContent side="right" className="w-[88vw] max-w-sm border-cyan-400/20 bg-ink text-white">
      <SheetTitle className="sr-only">Menu</SheetTitle>
      <div className="mt-10 flex flex-col gap-1">
        {[...LINKS, ...FEATURES.map((f) => ({ to: featurePath(f.slug), label: f.title, sub: true }))].map((l) => (
          <SheetClose asChild key={l.label}>
            <Link to={l.to} data-testid={`mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`rounded-xl px-3 py-3 transition-colors hover:bg-white/5 ${l.sub ? "pl-6 text-sm text-slate-300" : "font-display text-2xl font-semibold"}`}>{l.label}</Link>
          </SheetClose>
        ))}
        <SheetClose asChild>
          <Link to="/waitlist" data-testid="mobile-waitlist-btn" className="btn-primary mt-6">Join Waitlist <ArrowRight className="h-4 w-4" /></Link>
        </SheetClose>
      </div>
    </SheetContent>
  </Sheet>
);
